import { Component, HostListener, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  RouterOutlet,
  Router,
  RouterLink,
  RouterLinkActive,
  NavigationEnd,
} from '@angular/router';
import { filter } from 'rxjs/operators';
import { finalize } from 'rxjs/operators';
import { SessionStarterComponent } from './components/session-starter/session-starter.component';
import { CurrentSessionComponent } from './components/current-session/current-session.component';
import { MiniLeaderboardComponent } from './components/mini-leaderboard/mini-leaderboard.component';
import { LogoComponent } from './components/logo/logo.component';
import { SessionHistoryComponent } from './components/session-history/session-history.component';
import { SettingsModalComponent } from './components/settings-modal/settings-modal.component';
import { AuthService, User } from './services/auth.service';
import { LeaderboardService } from './services/leaderboard.service';
import { SessionService } from './services/session.service';
import { TeamService } from './services/team.service';
import { TeamContextService } from './services/team-context.service';
import { SessionResult, Team } from '@spectrace/shared';
import { SeasonService } from './services/season.service';
import { PosthogService } from './services/posthog.service';
import { FormsModule } from '@angular/forms';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    FormsModule,
    SessionStarterComponent,
    CurrentSessionComponent,
    MiniLeaderboardComponent,
    LogoComponent,
    SessionHistoryComponent,
    SettingsModalComponent,
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent {
  private static readonly COOKIE_CONSENT_STORAGE_KEY = 'cookie_consent_choice';

  title = 'SpecTrace Leaderboard';
  activeSession: SessionResult | null = null;
  showSessionModal = false;
  showCreateSeasonModal = false;
  showSettingsModal = false;
  currentView: 'leaderboard' | 'sessions' | 'seasons' = 'sessions';
  telemetryConnected = false;
  isAuthenticated = false;
  currentUser: User | null = null;
  telemetryClientDownloadUrl = environment.telemetryClientDownloadUrl;
  telemetryClientFallbackUrl = environment.telemetryClientFallbackUrl;
  telemetryDownloadStatus: 'idle' | 'starting' | 'opened' | 'blocked' = 'idle';
  showUserMenu = false;
  showTeamMenu = false;
  currentTeam: Team | null = null;
  userTeams: Team[] = [];
  public newSeasonName = '';
  public newSeasonDesc = '';
  public creatingSeason = false;
  public seasonCreationError: string | null = null;
  isOnSessionPage = false;
  isOnboardingPage = false;
  showCookieConsentBanner = true;
  private seasonModalBackdropMouseDown = false;
  private lastIdentifiedUserId: string | null = null;

  constructor(
    private leaderboardService: LeaderboardService,
    private sessionService: SessionService,
    private authService: AuthService,
    private teamService: TeamService,
    private teamContextService: TeamContextService,
    private seasonService: SeasonService,
    private posthogService: PosthogService,
    private router: Router,
  ) {
    this.initializeCookieConsent();

    // Track current route to hide banners on session page
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
      )
      .subscribe((event: NavigationEnd) => {
        this.isOnSessionPage = event.urlAfterRedirects.startsWith('/session/');
        this.isOnboardingPage =
          event.urlAfterRedirects.startsWith('/onboarding');

        this.enforceOnboardingRoute(event.urlAfterRedirects);

        if (
          !this.currentTeam &&
          this.isTeamOnlyRoute(event.urlAfterRedirects)
        ) {
          this.router.navigate(['/sessions']);
        }
      });

    this.authService.currentUser$.subscribe((user) => {
      this.isAuthenticated = !!user;
      this.currentUser = user;

      if (user) {
        if (this.lastIdentifiedUserId !== user.id) {
          this.posthogService.identify(user.id, {
            email: user.email,
            name: user.nickname || user.name,
            onboardingCompleted: user.onboardingCompleted,
            telemetryClientDownloadClicked: user.telemetryClientDownloadClicked,
            firstValidLapSubmittedAt: user.firstValidLapSubmittedAt,
            firstValidLapWithin24h: user.firstValidLapWithin24h,
            isAdmin: user.isAdmin,
          });
          this.posthogService.capture('user_authenticated', {
            source: 'auth_state_change',
          });
          this.lastIdentifiedUserId = user.id;
        }

        this.enforceOnboardingRoute(this.router.url);

        // Initialize services that make API calls only when authenticated
        this.leaderboardService.initialize();

        this.sessionService.activeSession$.subscribe((session) => {
          this.activeSession = session;
        });

        this.leaderboardService.telemetryStatus$.subscribe((connected) => {
          this.telemetryConnected = connected;
        });

        this.loadUserTeams();
      } else {
        if (this.lastIdentifiedUserId) {
          this.posthogService.capture('user_logged_out');
        }
        this.posthogService.reset();
        this.lastIdentifiedUserId = null;
        this.teamContextService.clear();
        this.userTeams = [];
        this.currentTeam = null;
        this.activeSession = null;
        this.telemetryConnected = false;
      }
    });

    this.teamContextService.currentTeam$.subscribe((team) => {
      this.currentTeam = team;
    });

    this.teamContextService.userTeams$.subscribe((teams) => {
      this.userTeams = teams;
    });
  }

  acceptAllCookies(): void {
    this.setCookieConsent('accept_all');
  }

  rejectNonEssentialCookies(): void {
    this.setCookieConsent('reject_non_essential');
  }

  private loadUserTeams(): void {
    this.teamService.getUserTeams().subscribe({
      next: (response) => {
        this.teamContextService.setUserTeams(response.teams);
      },
      error: (err) => {
        console.error('Failed to load teams:', err);
      },
    });
  }

  openSessionModal(): void {
    this.posthogService.capture('session_modal_opened', {
      hasTeamContext: !!this.currentTeam,
    });
    this.showSessionModal = true;
  }

  closeSessionModal(): void {
    this.showSessionModal = false;
  }

  openCreateSeasonModal(): void {
    this.seasonCreationError = null;
    this.posthogService.capture('season_create_modal_opened', {
      teamId: this.currentTeam?.id ?? null,
    });
    this.showCreateSeasonModal = true;
  }

  closeCreateSeasonModal(): void {
    this.showCreateSeasonModal = false;
    this.resetSeasonForm();
  }

  onSeasonModalMouseDown(event: MouseEvent): void {
    this.seasonModalBackdropMouseDown = event.target === event.currentTarget;
  }

  onSeasonModalOverlayClick(event: MouseEvent): void {
    const clickedBackdrop = event.target === event.currentTarget;
    if (clickedBackdrop && this.seasonModalBackdropMouseDown) {
      this.closeCreateSeasonModal();
    }
    this.seasonModalBackdropMouseDown = false;
  }

  closeSessionView(): void {
    this.router.navigate(['/sessions']);
  }

  onSessionStarted(sessionId: string): void {
    this.posthogService.capture('session_started', {
      sessionId,
    });
    this.closeSessionModal();
    this.router.navigate(['/session', sessionId]);
  }

  logout(): void {
    this.showUserMenu = false;
    this.posthogService.capture('logout_clicked');
    this.authService.logout();
    this.teamContextService.clear();
  }

  get showTelemetryClientDownloadBanner(): boolean {
    return (
      this.isAuthenticated &&
      !this.isOnSessionPage &&
      !!this.currentUser &&
      !this.currentUser.telemetryClientDownloadClicked
    );
  }

  get onboardingStepHasTeam(): boolean {
    return this.userTeams.length > 0;
  }

  get onboardingStepClientInstalled(): boolean {
    return !!this.currentUser?.telemetryClientDownloadClicked;
  }

  get onboardingStepFeedValidated(): boolean {
    return (
      !!this.currentUser?.firstValidLapSubmittedAt || this.telemetryConnected
    );
  }

  get onboardingStepJoinedSession(): boolean {
    return !!this.currentUser?.firstValidLapSubmittedAt || !!this.activeSession;
  }

  get currentUserDisplayName(): string {
    return (
      this.currentUser?.nickname?.trim() ||
      this.currentUser?.name?.trim() ||
      'Driver'
    );
  }

  get onboardingProgressCount(): number {
    return [
      this.onboardingStepHasTeam,
      this.onboardingStepClientInstalled,
      this.onboardingStepFeedValidated,
      this.onboardingStepJoinedSession,
    ].filter(Boolean).length;
  }

  downloadTelemetryClient(): void {
    this.telemetryDownloadStatus = 'starting';
    this.posthogService.capture('telemetry_client_download_clicked');

    const didStartDownload = this.startTelemetryClientDownload();
    this.telemetryDownloadStatus = didStartDownload ? 'opened' : 'blocked';

    if (!didStartDownload) {
      this.posthogService.capture('telemetry_client_download_failed', {
        reason: 'blocked_or_popup_prevented',
      });
      console.error('Telemetry client download could not be started.');
      return;
    }

    this.posthogService.capture('telemetry_client_download_started');
    this.finalizeTelemetryClientDownloadStep();
  }

  dismissTelemetryClientDownloadBanner(): void {
    this.posthogService.capture('telemetry_client_download_dismissed');
    this.finalizeTelemetryClientDownloadStep();
  }

  private finalizeTelemetryClientDownloadStep(): void {
    if (this.currentUser?.telemetryClientDownloadClicked) {
      return;
    }

    this.authService.markTelemetryClientDownloadClicked().subscribe({
      next: (user) => {
        this.currentUser = user;
      },
      error: (err) => {
        console.error(
          'Failed to persist telemetry client download click:',
          err,
        );
      },
    });
  }

  private startTelemetryClientDownload(): boolean {
    if (!this.telemetryClientDownloadUrl) {
      return false;
    }

    try {
      const link = document.createElement('a');
      link.href = this.telemetryClientDownloadUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.download = '';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return true;
    } catch (error) {
      console.error('Anchor-based telemetry download failed:', error);
      const openedWindow = window.open(
        this.telemetryClientDownloadUrl,
        '_blank',
        'noopener,noreferrer',
      );
      return !!openedWindow;
    }
  }

  toggleUserMenu(): void {
    this.showUserMenu = !this.showUserMenu;
    this.showTeamMenu = false;
  }

  toggleTeamMenu(): void {
    this.showTeamMenu = !this.showTeamMenu;
    this.showUserMenu = false;
  }

  selectTeam(team: Team): void {
    this.teamContextService.setCurrentTeam(team);
    this.showTeamMenu = false;
    this.posthogService.capture('team_selected', {
      teamId: team.id,
      teamName: team.name,
    });
    // Refresh leaderboard with new team context
    this.leaderboardService.refreshLeaderboard();
  }

  getTeamLogoUrl(team: Team | null | undefined): string | null {
    if (!team?.logoUrl) {
      return null;
    }

    const trimmedLogoUrl = team.logoUrl.trim();
    return trimmedLogoUrl.length > 0 ? trimmedLogoUrl : null;
  }

  getTeamInitial(teamName: string | null | undefined): string {
    const normalizedName = (teamName || '').trim();
    return normalizedName ? normalizedName.charAt(0).toUpperCase() : 'T';
  }

  openSettings(): void {
    this.showUserMenu = false;
    this.posthogService.capture('settings_modal_opened');
    this.showSettingsModal = true;
  }

  closeSettingsModal(): void {
    this.showSettingsModal = false;
    // Reload teams in case they were updated
    this.loadUserTeams();
  }

  navigateToSeasons(): void {
    if (this.currentTeam) {
      this.currentView = 'seasons';
      this.router.navigate(['/teams', this.currentTeam.id]);
    }
  }

  get isCreateSeasonDisabled(): boolean {
    return (
      this.creatingSeason || !this.newSeasonName.trim() || !this.currentTeam
    );
  }

  createSeason(): void {
    if (this.isCreateSeasonDisabled || !this.currentTeam) {
      this.posthogService.capture('season_create_blocked', {
        reason: this.isCreateSeasonDisabled
          ? 'invalid_form_or_loading'
          : 'no_team',
      });
      return;
    }

    this.creatingSeason = true;
    this.seasonCreationError = null;
    const trimmedName = this.newSeasonName.trim();
    const trimmedDescription = this.newSeasonDesc.trim();

    this.seasonService
      .createSeason(this.currentTeam.id, {
        name: trimmedName,
        description: trimmedDescription || undefined,
      })
      .pipe(
        finalize(() => {
          this.creatingSeason = false;
        }),
      )
      .subscribe({
        next: () => {
          this.posthogService.capture('season_created', {
            teamId: this.currentTeam?.id,
            nameLength: trimmedName.length,
            hasDescription: !!trimmedDescription,
          });
          this.closeCreateSeasonModal();
        },
        error: (err) => {
          this.posthogService.capture('season_create_failed', {
            message: err?.error?.error || err?.message || 'unknown_error',
          });
          console.error('Failed to create season:', err);
          this.seasonCreationError =
            'Failed to create season. Please try again.';
        },
      });
  }

  private isTeamOnlyRoute(url: string): boolean {
    return (
      url.startsWith('/leaderboard') ||
      url.startsWith('/best-laps') ||
      url.startsWith('/seasons') ||
      url.startsWith('/teams/')
    );
  }

  private enforceOnboardingRoute(currentUrl: string): void {
    const user = this.currentUser;
    if (!user) {
      return;
    }

    if (currentUrl.startsWith('/auth/callback')) {
      return;
    }

    const isOnboardingRoute = currentUrl.startsWith('/onboarding');

    if (!user.onboardingCompleted && !isOnboardingRoute) {
      this.router.navigate(['/onboarding']);
      return;
    }

    if (user.onboardingCompleted && isOnboardingRoute) {
      this.router.navigate(['/sessions']);
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const userMenuContainer = target.closest('.user-menu-container');
    const teamMenuContainer = target.closest('.team-selector');

    // Close the menus if clicking outside of them
    if (!userMenuContainer && this.showUserMenu) {
      this.showUserMenu = false;
    }
    if (!teamMenuContainer && this.showTeamMenu) {
      this.showTeamMenu = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscapePress(): void {
    if (this.showCreateSeasonModal) {
      this.closeCreateSeasonModal();
      return;
    }

    if (this.showSessionModal) {
      this.closeSessionModal();
    }
  }

  private resetSeasonForm(): void {
    this.newSeasonName = '';
    this.newSeasonDesc = '';
    this.seasonCreationError = null;
  }

  private initializeCookieConsent(): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      this.showCookieConsentBanner = true;
      return;
    }

    const storedConsent = window.localStorage.getItem(
      AppComponent.COOKIE_CONSENT_STORAGE_KEY,
    );

    if (!storedConsent) {
      this.showCookieConsentBanner = true;
      return;
    }

    try {
      const parsed = JSON.parse(storedConsent) as {
        choice?: string;
        timestamp?: string;
      };
      const hasValidChoice =
        parsed.choice === 'accept_all' ||
        parsed.choice === 'reject_non_essential';
      this.showCookieConsentBanner = !hasValidChoice || !parsed.timestamp;
    } catch {
      this.showCookieConsentBanner = true;
    }
  }

  private setCookieConsent(
    choice: 'accept_all' | 'reject_non_essential',
  ): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      this.showCookieConsentBanner = false;
      return;
    }

    window.localStorage.setItem(
      AppComponent.COOKIE_CONSENT_STORAGE_KEY,
      JSON.stringify({
        choice,
        timestamp: new Date().toISOString(),
      }),
    );
    this.showCookieConsentBanner = false;
  }
}
